import { useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Clock,
  ExternalLink,
  FileText,
  Filter,
  Monitor,
  RefreshCw,
  ScrollText,
  User,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/pwa/queryConfig';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ClickToCopy } from '@/components/ui/click-to-copy';
import { CopyButton } from '@/components/ui/copy-button';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/ui/PageHeader';
import { Spinner } from '@/components/ui/spinner';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { SearchHighlight } from '@/components/ui/search-highlight';
import { fullDate, relativeTime } from '@/lib/datetime';
import { useExpandSet } from '@/hooks/useExpandSet';
import type { LogEntry, RequestGroup, ServiceName } from '../../api/observability';
import { useLogQuery, useRequestQuery, useUserNames } from '../../hooks/useLogQuery';
import { ObservabilityFilterBar, type Period } from './components/ObservabilityFilterBar';

/* ------------------------------------------------------------------ */
/*  Constantes                                                        */
/* ------------------------------------------------------------------ */

const LEVEL_OPTIONS = ['ERROR', 'WARNING', 'INFO', 'DEBUG'] as const;

const CARD_BORDER = 'border border-gray-300 dark:border-gray-700';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function periodToStart(period: Period): string {
  const now = Date.now();
  const ms: Record<Period, number> = {
    '15m': 15 * 60_000,
    '1h': 60 * 60_000,
    '6h': 6 * 60 * 60_000,
    '24h': 24 * 60 * 60_000,
    '7d': 7 * 24 * 60 * 60_000,
  };
  return new Date(now - ms[period]).toISOString();
}

/** Formata timestamp como dd-MM-yyyy HH:mm:ss.SSS */
function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    const pad = (n: number, len = 2) => String(n).padStart(len, '0');
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
  } catch {
    return iso;
  }
}

/** Monta linha completa do log:
 *  timestamp | reqId user ip | LEVEL | METHOD /path | logger | Mensagem | key=value ...
 */
function formatLogLine(entry: LogEntry): string {
  const parts: string[] = [];

  // Timestamp
  parts.push(formatTimestamp(entry.timestamp));

  // Contexto da requisição: reqId + usuário + IP
  const ctx = [
    entry.request_id?.slice(0, 8),
    entry.user_tag && entry.user_tag !== '-' ? entry.user_tag : null,
    entry.client_ip,
  ].filter(Boolean);
  if (ctx.length > 0) parts.push(ctx.join(' '));

  // Nível
  parts.push(entry.level.toUpperCase());

  // Método + path HTTP
  if (entry.http_method || entry.http_path) {
    parts.push([entry.http_method, entry.http_path].filter(Boolean).join(' '));
  }

  // Logger (nome abreviado)
  if (entry.logger) parts.push(entry.logger);

  // Mensagem do evento + extras como parâmetros (chave=valor, chave2=valor2)
  const extraPairs = entry.extra
    ? Object.entries(entry.extra).filter(([, v]) => v != null && v !== '').map(([k, v]) => `${k}=${v}`)
    : [];
  parts.push(extraPairs.length > 0 ? `${entry.event}: ${extraPairs.join(', ')}` : entry.event);

  return parts.join(' | ');
}

import { classifyError } from '../../lib/errors';

/** Extrai o sistema operacional a partir do user agent. */
function parseOS(ua: string): string {
  if (/Windows NT 10/.test(ua)) return 'Windows 10/11';
  if (/Windows NT 6\.3/.test(ua)) return 'Windows 8.1';
  if (/Windows NT 6\.2/.test(ua)) return 'Windows 8';
  if (/Windows NT 6\.1/.test(ua)) return 'Windows 7';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Android\s?([\d.]+)?/.test(ua)) {
    const m = ua.match(/Android\s?([\d.]+)?/);
    return m?.[1] ? `Android ${m[1]}` : 'Android';
  }
  if (/iPhone OS ([\d_]+)/.test(ua)) {
    const m = ua.match(/iPhone OS ([\d_]+)/);
    return m?.[1] ? `iOS ${m[1].replace(/_/g, '.')}` : 'iOS';
  }
  if (/iPad/.test(ua)) return 'iPadOS';
  if (/Mac OS X ([\d_.]+)/.test(ua)) {
    const m = ua.match(/Mac OS X ([\d_.]+)/);
    return m?.[1] ? `macOS ${m[1].replace(/_/g, '.')}` : 'macOS';
  }
  if (/Mac OS X/.test(ua) || /Macintosh/.test(ua)) return 'macOS';
  if (/CrOS/.test(ua)) return 'Chrome OS';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Desconhecido';
}

/* ------------------------------------------------------------------ */
/*  Badge helpers                                                      */
/* ------------------------------------------------------------------ */

const LEVEL_BORDER: Record<string, string> = {
  ERROR: 'border-l-red-400',
  WARNING: 'border-l-amber-400',
  INFO: 'border-l-blue-400',
  DEBUG: 'border-l-gray-300 dark:border-l-gray-600',
};

const LEVEL_BADGE_STYLES: Record<string, string> = {
  ERROR: 'bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400',
  WARNING: 'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400',
  INFO: 'bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400',
  DEBUG: 'bg-gray-100 text-gray-500 dark:bg-gray-800/50 dark:text-gray-400',
};

function LevelBadge({ level }: { level: string }) {
  const upper = level.toUpperCase();
  return (
    <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[11px] font-semibold leading-none ${LEVEL_BADGE_STYLES[upper] || LEVEL_BADGE_STYLES.DEBUG}`}>
      {upper}
    </span>
  );
}

function HttpStatusBadge({ status }: { status?: number }) {
  if (status == null) return null;
  const cls =
    status >= 400 ? 'bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400'
    : status >= 200 ? 'bg-green-50 text-green-600 dark:bg-green-950/40 dark:text-green-400'
    : 'bg-gray-100 text-muted-foreground';
  return (
    <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 font-mono text-[11px] font-semibold leading-none ${cls}`}>
      {status}
    </span>
  );
}

const METHOD_STYLES: Record<string, string> = {
  GET: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400',
  POST: 'bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-400',
  PUT: 'bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400',
  PATCH: 'bg-purple-100 text-purple-700 dark:bg-purple-950/50 dark:text-purple-400',
  DELETE: 'bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400',
};

function MethodBadge({ method }: { method?: string }) {
  if (!method) return null;
  const upper = method.toUpperCase();
  return (
    <span className={`inline-flex shrink-0 items-center rounded px-2 py-0.5 font-mono text-[11px] font-bold leading-none ${METHOD_STYLES[upper] || 'bg-gray-100 text-gray-600'}`}>
      {upper}
    </span>
  );
}

const SERVICE_LABEL: Record<ServiceName, string> = {
  jurisapp: 'JurisApp',
  autenticador: 'Autenticador',
};

const SERVICE_STYLES: Record<ServiceName, string> = {
  jurisapp: 'bg-gray-100 text-gray-700 dark:bg-gray-800/60 dark:text-gray-300',
  autenticador: 'bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300',
};

function ServiceBadge({ service }: { service?: ServiceName | string }) {
  if (!service) return null;
  const isKnown = (s: string): s is ServiceName => s === 'jurisapp' || s === 'autenticador';
  const cls = isKnown(service) ? SERVICE_STYLES[service] : 'bg-gray-100 text-gray-700';
  const label = isKnown(service) ? SERVICE_LABEL[service] : service;
  return (
    <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[11px] font-medium leading-none ${cls}`}>
      {label}
    </span>
  );
}

function AlvoBadge({ alvo }: { alvo?: string }) {
  if (!alvo) return null;
  return (
    <span className="inline-flex shrink-0 items-center rounded bg-violet-50 px-1.5 py-0.5 font-mono text-[11px] text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
      {alvo}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared small components                                            */
/* ------------------------------------------------------------------ */

function RelativeTimestamp({ iso }: { iso: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="shrink-0 cursor-help whitespace-nowrap font-mono text-xs text-muted-foreground">
          {relativeTime(iso)}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top"><span className="text-xs">{fullDate(iso)}</span></TooltipContent>
    </Tooltip>
  );
}

function UserDisplay({ tag, resolved }: { tag?: string; resolved?: Record<string, string> }) {
  if (!tag || tag === '-') return null;
  const name = resolved?.[tag] || tag;
  return (
    <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
      <User className="h-3 w-3 shrink-0" />
      <span className="break-words">{name}</span>
    </span>
  );
}

function ExpandCollapseToggle({ chevron }: { chevron: 'down' | 'right' }) {
  return chevron === 'down'
    ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
    : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />;
}

function CardLoading() {
  return (
    <div className="flex justify-center py-12">
      <Spinner size={32} label="Carregando dados" />
    </div>
  );
}


/* ------------------------------------------------------------------ */
/*  Stats Bar (aba Logs)                                               */
/* ------------------------------------------------------------------ */

function StatsBar({ entries, activeLevel, onLevelClick }: {
  entries: LogEntry[]; activeLevel: string; onLevelClick: (l: string) => void;
}) {
  const counts = useMemo(() => {
    const c: Record<string, number> = { ERROR: 0, WARNING: 0, INFO: 0, DEBUG: 0 };
    for (const e of entries) { const l = e.level.toUpperCase(); if (l in c) c[l]++; }
    return c;
  }, [entries]);

  const DOT: Record<string, string> = {
    ERROR: 'bg-red-500',
    WARNING: 'bg-amber-500',
    INFO: 'bg-blue-500',
    DEBUG: 'bg-gray-400 dark:bg-gray-500',
  };

  const ACTIVE: Record<string, string> = {
    ALL: 'bg-background text-foreground shadow-sm',
    ERROR: 'bg-red-50 text-red-700 shadow-sm dark:bg-red-950/40 dark:text-red-300',
    WARNING: 'bg-amber-50 text-amber-700 shadow-sm dark:bg-amber-950/40 dark:text-amber-300',
    INFO: 'bg-blue-50 text-blue-700 shadow-sm dark:bg-blue-950/40 dark:text-blue-300',
    DEBUG: 'bg-background text-foreground shadow-sm',
  };

  const pill = 'cursor-pointer rounded-md px-3 py-1.5 text-xs font-medium transition-all';
  const idle = 'text-muted-foreground hover:bg-background/50 hover:text-foreground';

  return (
    <div className="inline-flex flex-wrap items-center gap-1 rounded-lg bg-muted/50 p-1">
      <button onClick={() => onLevelClick('ALL')} className={`${pill} ${activeLevel === 'ALL' ? ACTIVE.ALL : idle}`}>
        Todos <span className="ml-0.5 tabular-nums">{entries.length}</span>
      </button>
      {LEVEL_OPTIONS.map((lvl) => (
        <button key={lvl} onClick={() => onLevelClick(lvl)}
          className={`flex items-center gap-1.5 ${pill} ${activeLevel === lvl ? ACTIVE[lvl] : idle}`}
        >
          <span className={`h-2 w-2 rounded-full ${DOT[lvl]}`} />
          {lvl}
          <span className="tabular-nums">{counts[lvl]}</span>
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Log Card                                                          */
/* ------------------------------------------------------------------ */

function LogCard({ entry, isExpanded, onToggle, userNames, onFilterUser, onFilterReqId, searchQuery }: {
  entry: LogEntry; isExpanded: boolean; onToggle: () => void;
  userNames?: Record<string, string>;
  onFilterUser?: (tag: string) => void; onFilterReqId?: (id: string) => void;
  searchQuery?: string;
}) {
  const upper = entry.level.toUpperCase();
  const borderCls = LEVEL_BORDER[upper] || 'border-l-gray-300';

  return (
    <div className={`overflow-hidden rounded-lg ${CARD_BORDER} border-l-4 bg-card ${borderCls}`}>
      <div
        role="button" tabIndex={0} onClick={onToggle}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
        className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-accent"
      >
        <RelativeTimestamp iso={entry.timestamp} />
        <LevelBadge level={entry.level} />
        <ServiceBadge service={entry.service} />
        <AlvoBadge alvo={entry.alvo} />
        <span className="min-w-0 flex-1 break-all font-mono text-sm text-foreground">
          <SearchHighlight text={entry.event} query={searchQuery} />
        </span>
        <UserDisplay tag={entry.user_tag} resolved={userNames} />
        {entry.request_id && (
          <ClickToCopy
            text={entry.request_id}
            display={<SearchHighlight text={entry.request_id.slice(0, 8)} query={searchQuery} />}
            className="hidden shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground sm:inline"
          />
        )}
        <ExpandCollapseToggle chevron={isExpanded ? 'down' : 'right'} />
      </div>

      {isExpanded && (() => {
        const logLine = formatLogLine(entry);
        return (
          <div className="border-t border-border bg-background px-4 py-3">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex flex-wrap items-center gap-4">
                {entry.user_tag && entry.user_tag !== '-' && onFilterUser && (
                  <button onClick={() => onFilterUser(entry.user_tag!)} className="flex cursor-pointer items-center gap-1 text-xs text-primary hover:underline">
                    <Filter className="h-3 w-3" />Filtrar por usuário
                  </button>
                )}
                {entry.request_id && onFilterReqId && (
                  <button onClick={() => onFilterReqId(entry.request_id!)} className="flex cursor-pointer items-center gap-1 text-xs text-primary hover:underline">
                    <Filter className="h-3 w-3" />Filtrar por reqId
                  </button>
                )}
              </div>
              <CopyButton text={logLine} label="Copiar" />
            </div>
            <div className="rounded border border-border bg-card px-3 py-2.5">
              <p className="font-mono text-xs leading-relaxed text-foreground">
                <SearchHighlight text={logLine} query={searchQuery} />
              </p>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 sm:hidden">
              {entry.user_tag && entry.user_tag !== '-' && (
                <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-1 text-xs">
                  <User className="h-3 w-3 text-muted-foreground" />{userNames?.[entry.user_tag] || entry.user_tag}
                </span>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Request Card                                                      */
/* ------------------------------------------------------------------ */

function RequestCard({ group, isSelected, onToggle, userNames, searchQuery, windowStart, windowEnd }: {
  group: RequestGroup; isSelected: boolean; onToggle: () => void;
  userNames?: Record<string, string>;
  searchQuery?: string;
  windowStart?: string;
  windowEnd?: string;
}) {
  const hasError = group.error_count > 0;
  // Expand por hop: filtra por (service, rid) E pela janela temporal vinda de
  // RequestsTab (mesmo critério do bucketing do backend). Sem janela, o expand
  // traria logs de todas as transações HTTP que compartilham o reqId (caso
  // típico: jurisapp dispara várias chamadas ao autenticador no mesmo handler).
  const { data: requestLogs, isLoading: isLoadingLogs } = useLogQuery(
    {
      request_id: group.request_id,
      service: group.service,
      start: windowStart,
      end: windowEnd,
      limit: 500,
    },
    isSelected,
  );
  // Ordem cronológica (mais antigo → mais recente) para acompanhar o fluxo da requisição
  const entries = requestLogs?.entries;
  const chronologicalEntries = useMemo(
    () => entries ? [...entries].reverse() : [],
    [entries],
  );

  return (
    <div className={`overflow-hidden rounded-lg ${CARD_BORDER} bg-card ${hasError ? 'border-l-4 border-l-red-400' : ''}`}>
      <div
        role="button" tabIndex={0} onClick={onToggle}
        onKeyDown={(e) => e.key === 'Enter' && onToggle()}
        className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-accent"
      >
        <MethodBadge method={group.method} />
        <ServiceBadge service={group.service} />
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="break-all font-mono text-sm text-foreground">
            <SearchHighlight text={group.path || '-'} query={searchQuery} />
          </span>
          <HttpStatusBadge status={group.status} />
        </div>
        <ClickToCopy
          text={group.request_id}
          display={<SearchHighlight text={group.request_id.slice(0, 8)} query={searchQuery} />}
          className="hidden shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground md:inline"
        />
        {group.duration_ms != null && (
          <span className="hidden items-center gap-1 whitespace-nowrap font-mono text-xs text-muted-foreground md:inline-flex">
            <Clock className="h-3 w-3" />{Math.round(group.duration_ms)}ms
          </span>
        )}
        <span className="hidden items-center gap-1 text-xs text-muted-foreground md:inline-flex">
          <FileText className="h-3 w-3" />{group.log_count}
        </span>
        {group.error_count > 0 && (
          <span className="hidden items-center gap-1 text-xs text-red-600 dark:text-red-400 md:inline-flex">
            <AlertTriangle className="h-3 w-3" />{group.error_count}
          </span>
        )}
        <UserDisplay tag={group.user_tag} resolved={userNames} />
        {group.timestamp && <RelativeTimestamp iso={group.timestamp} />}
        <ExpandCollapseToggle chevron={isSelected ? 'down' : 'right'} />
      </div>

      {/* Mobile meta */}
      <div className="flex flex-wrap items-center gap-2 px-4 pb-2 md:hidden">
        {group.duration_ms != null && (
          <span className="flex items-center gap-1 font-mono text-xs text-muted-foreground"><Clock className="h-3 w-3" />{Math.round(group.duration_ms)}ms</span>
        )}
        <span className="flex items-center gap-1 text-xs text-muted-foreground"><FileText className="h-3 w-3" />{group.log_count} logs</span>
        {group.error_count > 0 && (
          <span className="flex items-center gap-1 text-xs text-red-600"><AlertTriangle className="h-3 w-3" />{group.error_count} erros</span>
        )}
      </div>

      {isSelected && (
        <div className="border-t border-border bg-background p-3">
          <div className="mb-3 flex items-center justify-between rounded border border-border bg-card px-3 py-2">
            <span className="flex items-center gap-2 text-xs text-muted-foreground">
              <ScrollText className="h-3.5 w-3.5" />
              {chronologicalEntries.length || '...'} logs
            </span>
            {chronologicalEntries.length > 0 && (
              <CopyButton
                text={chronologicalEntries.map((e) => formatLogLine(e)).join('\n')}
                label="Copiar todos"
              />
            )}
          </div>
          <div className="max-h-[500px] space-y-2 overflow-auto">
            {isLoadingLogs ? (
              <CardLoading />
            ) : chronologicalEntries.length === 0 ? (
              <p className="py-4 text-center text-xs text-muted-foreground">Nenhum log encontrado</p>
            ) : (
              chronologicalEntries.map((entry: LogEntry, idx: number) => {
                const bCls = LEVEL_BORDER[entry.level.toUpperCase()] || 'border-l-gray-300';
                const logLine = formatLogLine(entry);
                return (
                  <div key={idx} className={`rounded border border-border border-l-[3px] bg-card ${bCls} px-3 py-2.5`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="shrink-0 font-mono text-[11px] text-muted-foreground">{fullDate(entry.timestamp)}</span>
                        <LevelBadge level={entry.level} />
                      </div>
                      <CopyButton text={logLine} label="Copiar" className="h-6 w-6" />
                    </div>
                    <p className="mt-1 font-mono text-xs leading-relaxed text-foreground">
                      <SearchHighlight text={logLine} query={searchQuery} />
                    </p>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  URL sync helper                                                    */
/* ------------------------------------------------------------------ */

/** Hook para um filtro sincronizado com query string.
 * Default genérico é `string`; anotar T explicitamente quando o filtro é union literal.
 */
function useUrlState<T extends string = string>(
  key: string,
  defaultValue: string,
): [T, (value: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(key);
  const value = (raw ?? defaultValue) as T;
  const setValue = useCallback(
    (next: T) => {
      setSearchParams(
        (prev) => {
          const np = new URLSearchParams(prev);
          if (!next || next === defaultValue) np.delete(key);
          else np.set(key, next);
          return np;
        },
        { replace: true },
      );
    },
    [key, defaultValue, setSearchParams],
  );
  return [value, setValue];
}

/* ------------------------------------------------------------------ */
/*  Tab: Logs                                                         */
/* ------------------------------------------------------------------ */

/** Detecta se a string é um request_id puro (UUID completo ou prefixo curto hex de 6+ chars).
 * Quando for, manda como request_id estruturado (label selector no Loki) em vez de line filter.
 */
function isLikelyRequestId(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  // UUID v4 completo
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)) return true;
  // Prefixo curto do jurisapp (8 chars hex, exibido como reqId.slice(0,8))
  if (/^[0-9a-f]{6,12}$/i.test(trimmed)) return true;
  return false;
}

function LogsTab() {
  const [level, setLevel] = useUrlState('level', 'ERROR');
  const [search, setSearch] = useUrlState('search', '');
  const [userFilter, setUserFilter] = useUrlState('user', '');
  const [period, setPeriod] = useUrlState<Period>('period', '1h');
  const [limitStr, setLimitStr] = useUrlState('limit', '100');
  const [serviceFilter, setServiceFilter] = useUrlState<ServiceName | 'all'>('service', 'all');
  const [alvoFilter, setAlvoFilter] = useUrlState('alvo', '');
  const [operationFilter, setOperationFilter] = useUrlState('operation', '');
  const [reqIdFromUrl, setReqIdFromUrl] = useUrlState('reqId', '');
  const [desde, setDesde] = useUrlState('desde', '');
  const [ate, setAte] = useUrlState('ate', '');
  const limit = Number(limitStr) || 100;
  const { expandedSet, toggle, expandAll, collapseAll } = useExpandSet();

  // Param `reqId=` no URL (vindo de drill-down) tem precedência sobre detecção via search.
  // Caso contrário, se search parece um request_id, promove a filtro estruturado.
  const requestIdFilter = reqIdFromUrl || (isLikelyRequestId(search) ? search.trim() : '');
  const setRequestIdFilter = (id: string) => {
    setReqIdFromUrl(id);
    if (id) setSearch('');
  };

  const params = useMemo(() => {
    const useAbsolute = Boolean(desde && ate);
    // Se search virou request_id, não enviar como search também (evita line filter redundante).
    const searchForBackend = requestIdFilter ? undefined : search || undefined;
    return {
      search: searchForBackend,
      request_id: requestIdFilter || undefined,
      user_search: userFilter || undefined,
      service: serviceFilter !== 'all' ? serviceFilter : undefined,
      alvo: alvoFilter || undefined,
      operation: operationFilter || undefined,
      limit,
      start: useAbsolute ? desde : periodToStart(period),
      end: useAbsolute ? ate : undefined,
    };
  }, [search, requestIdFilter, userFilter, serviceFilter, alvoFilter, operationFilter, limit, period, desde, ate]);

  const { data, isLoading, isFetching, error } = useLogQuery(params, true);

  const showLoading = isLoading || (isFetching && !data);
  const allEntries = useMemo(() => data?.entries || [], [data?.entries]);
  const filtered = useMemo(
    () => (level === 'ALL' ? allEntries : allEntries.filter((e) => e.level.toUpperCase() === level)),
    [allEntries, level],
  );

  const userTags = useMemo(() => allEntries.map((e) => e.user_tag).filter(Boolean) as string[], [allEntries]);
  const { data: userNames } = useUserNames(userTags);

  return (
    <div className="space-y-4">
      <ObservabilityFilterBar
        search={search} onSearchChange={setSearch}
        searchPlaceholder="Texto, endpoint ou reqId completo..."
        userFilter={userFilter} onUserFilterChange={setUserFilter}
        serviceFilter={serviceFilter} onServiceFilterChange={setServiceFilter}
        alvoFilter={alvoFilter} onAlvoFilterChange={setAlvoFilter}
        operationFilter={operationFilter} onOperationFilterChange={setOperationFilter}
        period={period} onPeriodChange={setPeriod}
        limit={limit} onLimitChange={(n) => setLimitStr(String(n))} limitLabel="logs"
      />

      {error && !showLoading && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}

      {requestIdFilter && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-900 dark:bg-blue-950/30">
          <Filter className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
          <span className="text-xs text-blue-700 dark:text-blue-300">
            Filtrando por reqId: <span className="font-mono font-semibold">{requestIdFilter.slice(0, 8)}</span>
          </span>
          <Button
            variant="link"
            size="sm"
            onClick={() => setRequestIdFilter('')}
            className="ml-auto h-auto p-0 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Limpar
          </Button>
        </div>
      )}

      {desde && ate && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          <Filter className="h-3.5 w-3.5" />
          <span>Período absoluto ativo — seletor de período relativo ignorado</span>
          <button onClick={() => { setDesde(''); setAte(''); }} className="ml-auto hover:underline">Limpar</button>
        </div>
      )}

      {!showLoading && allEntries.length > 0 && <StatsBar entries={allEntries} activeLevel={level} onLevelClick={setLevel} />}

      {!showLoading && filtered.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {filtered.length} {filtered.length === 1 ? 'log' : 'logs'}
            {level !== 'ALL' && allEntries.length !== filtered.length && (
              <span className="ml-1 text-xs">
                ({level} de {allEntries.length} no período)
              </span>
            )}
          </p>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={expandedSet.size > 0 ? collapseAll : () => expandAll(filtered.length)}>
            {expandedSet.size > 0 ? <><ChevronsDownUp className="mr-1 h-3.5 w-3.5" />Recolher todos</> : <><ChevronsUpDown className="mr-1 h-3.5 w-3.5" />Expandir todos</>}
          </Button>
        </div>
      )}

      {showLoading ? <CardLoading /> : filtered.length === 0 ? (
        <EmptyState icon={<FileText />} title="Nenhum log encontrado" />
      ) : (
        <div className="space-y-2">
          {filtered.map((entry, idx) => (
            <LogCard
              key={`${entry.timestamp}-${idx}`} entry={entry}
              isExpanded={expandedSet.has(idx)} onToggle={() => toggle(idx)}
              userNames={userNames}
              onFilterUser={(tag) => { setUserFilter(tag); setSearch(''); setRequestIdFilter(''); }}
              onFilterReqId={(id) => { setRequestIdFilter(id); setUserFilter(''); setLevel('ALL'); }}
              searchQuery={search}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab: Requisições                                                  */
/* ------------------------------------------------------------------ */

function RequestsTab() {
  const [search, setSearch] = useUrlState('search', '');
  const [userFilter, setUserFilter] = useUrlState('user', '');
  const [period, setPeriod] = useUrlState<Period>('period', '1h');
  const [limitStr, setLimitStr] = useUrlState('limit', '100');
  const [serviceFilter, setServiceFilter] = useUrlState<ServiceName | 'all'>('service', 'all');
  const [alvoFilter, setAlvoFilter] = useUrlState('alvo', '');
  const [operationFilter, setOperationFilter] = useUrlState('operation', '');
  const [reqIdFromUrl] = useUrlState('reqId', '');
  // setSearchParams direto: precisamos limpar reqId E search no MESMO callback.
  // Chamadas funcionais sequenciais a setSearchParams não compõem (react-router
  // lê o mesmo searchParamsRef.current nas duas), e a segunda sobrescreve a
  // primeira mantendo o filtro original. Inline com um único navigate resolve.
  const [, setSearchParams] = useSearchParams();
  const [errorsOnly, setErrorsOnly] = useState(false);
  // Chave de seleção combina serviço + reqId — backend agora retorna um grupo
  // por hop (jurisapp e autenticador podem compartilhar o mesmo request_id),
  // então usar só o reqId expandiria os dois cards juntos.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const limit = Number(limitStr) || 100;

  const reqIdFilter = reqIdFromUrl || (isLikelyRequestId(search) ? search.trim() : '');

  const params = useMemo(() => {
    const searchForBackend = reqIdFilter ? undefined : search || undefined;
    return {
      search: searchForBackend,
      user_search: userFilter || undefined,
      service: serviceFilter !== 'all' ? serviceFilter : undefined,
      alvo: alvoFilter || undefined,
      operation: operationFilter || undefined,
      request_id: reqIdFilter || undefined,
      limit,
      start: periodToStart(period),
    };
  }, [search, userFilter, serviceFilter, alvoFilter, operationFilter, reqIdFilter, limit, period]);

  const { data, isLoading, isFetching, error } = useRequestQuery(params, true);

  const hasError = useCallback((g: RequestGroup) => g.error_count > 0 || (g.status != null && g.status >= 400), []);
  const showLoading = isLoading || (isFetching && !data);
  const groups = useMemo(() => {
    const all = data?.groups || [];
    return errorsOnly ? all.filter(hasError) : all;
  }, [data?.groups, errorsOnly, hasError]);

  const userTags = useMemo(() => (data?.groups || []).map((g) => g.user_tag).filter(Boolean) as string[], [data?.groups]);
  const { data: userNames } = useUserNames(userTags);

  const summary = useMemo(() => {
    const all = data?.groups || [];
    return {
      requests: all.length,
      logs: all.reduce((a, g) => a + g.log_count, 0),
      errorLogs: all.reduce((a, g) => a + g.error_count, 0),
      warnLogs: all.reduce((a, g) => a + g.warn_count, 0),
      errors: all.filter(hasError).length,
    };
  }, [data?.groups, hasError]);

  // Janelas temporais por hop (mesmo critério do bucketing do backend):
  // - hop[i].start = hop[i-1].timestamp + 1ms (ou hop[i].timestamp - duration_ms - 1s no primeiro)
  // - hop[i].end   = hop[i].timestamp + 1ms (ou undefined no último, p/ capturar workers)
  // Calculado uma vez por mudança de groups; o RequestCard só consome.
  const hopWindows = useMemo(() => {
    const out = new Map<string, { start?: string; end?: string }>();
    const traces = new Map<string, RequestGroup[]>();
    for (const g of data?.groups ?? []) {
      const k = `${g.service ?? '-'}:${g.request_id}`;
      const arr = traces.get(k) ?? [];
      arr.push(g);
      traces.set(k, arr);
    }
    const shiftMs = (iso: string, deltaMs: number): string => {
      const d = new Date(iso).getTime();
      return Number.isFinite(d) ? new Date(d + deltaMs).toISOString() : iso;
    };
    for (const trace of traces.values()) {
      trace.sort((a, b) => (a.timestamp ?? '').localeCompare(b.timestamp ?? ''));
      for (let i = 0; i < trace.length; i++) {
        const hop = trace[i];
        const prev = i > 0 ? trace[i - 1] : null;
        const isLast = i === trace.length - 1;
        let start: string | undefined;
        if (prev?.timestamp) {
          start = shiftMs(prev.timestamp, 1);
        } else if (hop.timestamp) {
          const cushion = (hop.duration_ms ?? 0) + 1000;
          start = shiftMs(hop.timestamp, -cushion);
        }
        const end = isLast || !hop.timestamp ? undefined : shiftMs(hop.timestamp, 1);
        const key = `${hop.service ?? '-'}:${hop.request_id}:${hop.timestamp ?? ''}`;
        out.set(key, { start, end });
      }
    }
    return out;
  }, [data?.groups]);

  return (
    <div className="space-y-4">
      <ObservabilityFilterBar
        search={search} onSearchChange={setSearch}
        searchPlaceholder="Endpoint, reqId ou texto..."
        userFilter={userFilter} onUserFilterChange={setUserFilter}
        serviceFilter={serviceFilter} onServiceFilterChange={setServiceFilter}
        alvoFilter={alvoFilter} onAlvoFilterChange={setAlvoFilter}
        operationFilter={operationFilter} onOperationFilterChange={setOperationFilter}
        period={period} onPeriodChange={setPeriod}
        limit={limit} onLimitChange={(n) => setLimitStr(String(n))} limitLabel="requisições"
        errorsOnly={errorsOnly} onErrorsOnlyChange={setErrorsOnly}
      />

      {error && !showLoading && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}

      {reqIdFilter && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-900 dark:bg-blue-950/30">
          <Filter className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
          <span className="text-xs text-blue-700 dark:text-blue-300">
            Filtrando por reqId: <span className="font-mono font-semibold">{reqIdFilter.slice(0, 8)}</span>
          </span>
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setSearchParams((prev) => {
                const np = new URLSearchParams(prev);
                np.delete('reqId');
                np.delete('search');
                return np;
              }, { replace: true });
            }}
            className="ml-auto h-auto p-0 text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Limpar
          </Button>
        </div>
      )}

      {showLoading ? <CardLoading /> : groups.length === 0 ? (
        <EmptyState icon={<Activity />} title="Nenhuma requisição encontrada" />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Activity className="h-3.5 w-3.5" />
              {summary.requests} {summary.requests === 1 ? 'requisição' : 'requisições'}
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex cursor-help items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" />
                  {summary.logs} {summary.logs === 1 ? 'log' : 'logs'} agregados
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <span className="text-xs">
                  Soma de logs de todos os níveis dentro destas requisições.
                  {summary.errorLogs > 0 || summary.warnLogs > 0 ? (
                    <>
                      <br />
                      {summary.errorLogs} ERROR · {summary.warnLogs} WARN · {summary.logs - summary.errorLogs - summary.warnLogs} INFO/DEBUG
                    </>
                  ) : null}
                </span>
              </TooltipContent>
            </Tooltip>
            {summary.errors > 0 && (
              <button
                type="button"
                onClick={() => setErrorsOnly((v) => !v)}
                className={`flex items-center gap-1.5 rounded-md px-2 py-0.5 text-red-600 transition-colors hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-950 ${errorsOnly ? 'bg-red-100 ring-1 ring-red-300 dark:bg-red-950 dark:ring-red-700' : ''}`}
              >
                <AlertTriangle className="h-3.5 w-3.5" />{summary.errors} com {summary.errors === 1 ? 'erro' : 'erros'}
              </button>
            )}
          </div>
          <div className="space-y-2">
            {groups.map((g) => {
              const groupKey = `${g.service ?? '-'}:${g.request_id}:${g.timestamp ?? ''}`;
              const w = hopWindows.get(groupKey);
              return (
                <RequestCard key={groupKey} group={g}
                  isSelected={selectedKey === groupKey}
                  onToggle={() => setSelectedKey((prev) => prev === groupKey ? null : groupKey)}
                  userNames={userNames}
                  searchQuery={search}
                  windowStart={w?.start}
                  windowEnd={w?.end}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tab: Erros Frontend                                               */
/* ------------------------------------------------------------------ */

function FrontendErrorsTab() {
  const [search, setSearch] = useUrlState('search', '');
  const [userFilter, setUserFilter] = useUrlState('user', '');
  const [period, setPeriod] = useUrlState<Period>('period', '24h');
  const [limitStr, setLimitStr] = useUrlState('limit', '100');
  const limit = Number(limitStr) || 100;
  const { expandedSet, toggle } = useExpandSet();

  const params = useMemo(() => ({
    event: 'frontend_error',
    search: search || undefined,
    user_search: userFilter || undefined,
    limit,
    start: periodToStart(period),
  }), [search, userFilter, limit, period]);

  const { data, isLoading, isFetching, error } = useLogQuery(params, true);

  const showLoading = isLoading || (isFetching && !data);
  const entries = useMemo(() => data?.entries || [], [data?.entries]);

  const userTags = useMemo(() => entries.map((e) => e.user_tag).filter(Boolean) as string[], [entries]);
  const { data: userNames } = useUserNames(userTags);

  return (
    <div className="space-y-4">
      <ObservabilityFilterBar
        search={search} onSearchChange={setSearch}
        searchPlaceholder="Mensagem de erro..."
        userFilter={userFilter} onUserFilterChange={setUserFilter}
        period={period} onPeriodChange={setPeriod}
        limit={limit} onLimitChange={(n) => setLimitStr(String(n))} limitLabel="erros"
      />

      {error && !showLoading && (
        <Alert variant="destructive">
          <AlertDescription>{classifyError(error).message}</AlertDescription>
        </Alert>
      )}

      {showLoading ? <CardLoading /> : entries.length === 0 ? (
        <EmptyState icon={<AlertTriangle />} title="Nenhum erro de frontend encontrado" />
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{entries.length} {entries.length === 1 ? 'erro encontrado' : 'erros encontrados'}</p>
          <div className="space-y-2">
            {entries.map((entry, idx) => {
              const isExpanded = expandedSet.has(idx);
              const extra = entry.extra || {};
              const stack = typeof extra.stack === 'string' ? extra.stack : null;
              const source = typeof extra.source === 'string' ? extra.source : null;
              const url = typeof extra.url === 'string' ? extra.url : null;
              const userAgent = typeof extra.user_agent === 'string' ? extra.user_agent : null;
              const screenRes = typeof extra.screen_resolution === 'string' ? extra.screen_resolution : null;
              const osName = userAgent ? parseOS(userAgent) : null;

              return (
                <div key={`${entry.timestamp}-${idx}`} className={`overflow-hidden rounded-lg ${CARD_BORDER} border-l-4 border-l-red-400 bg-card`}>
                  <div role="button" tabIndex={0} onClick={() => toggle(idx)} onKeyDown={(e) => e.key === 'Enter' && toggle(idx)}
                    className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-accent">
                    <RelativeTimestamp iso={entry.timestamp} />
                    <LevelBadge level="ERROR" />
                    <span className="min-w-0 flex-1 break-words text-sm text-foreground">{typeof extra.message === 'string' ? extra.message : entry.event}</span>
                    <UserDisplay tag={entry.user_tag} resolved={userNames} />
                    <ExpandCollapseToggle chevron={isExpanded ? 'down' : 'right'} />
                  </div>

                  {isExpanded && (
                    <div className="border-t border-border bg-background px-4 py-3">
                      <div className="mb-3 flex flex-wrap gap-2">
                        {osName && <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-1 text-xs"><Monitor className="h-3 w-3 text-muted-foreground" /> {osName}</span>}
                        {source && <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-1 text-xs"><span className="text-muted-foreground">Origem:</span> {source}</span>}
                        {url && <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-1 text-xs"><span className="text-muted-foreground">URL:</span><span className="break-all">{url}</span></span>}
                        {entry.request_id && <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-1 font-mono text-xs">ReqID: {entry.request_id.slice(0, 12)}</span>}
                        {screenRes && <span className="inline-flex items-center gap-1 rounded bg-muted px-2 py-1 text-xs"><span className="text-muted-foreground">Tela:</span> {screenRes}</span>}
                        <UserDisplay tag={entry.user_tag} resolved={userNames} />
                      </div>
                      {userAgent && (
                        <div className="mb-3 rounded border border-border bg-muted/30 px-3 py-2">
                          <p className="text-[11px] font-medium text-muted-foreground">User Agent</p>
                          <p className="mt-0.5 break-all font-mono text-xs text-foreground">{userAgent}</p>
                        </div>
                      )}
                      {stack && (
                        <pre className="mb-3 overflow-auto whitespace-pre-wrap rounded border border-border bg-card p-3 font-mono text-xs leading-relaxed text-red-700 dark:text-red-400">{stack}</pre>
                      )}
                      <CopyButton text={JSON.stringify(entry, null, 2)} label="Copiar JSON" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page                                                         */
/* ------------------------------------------------------------------ */

export default function ObservabilityPage() {
  const queryClient = useQueryClient();
  const grafanaUrl = import.meta.env.VITE_GRAFANA_URL || '';
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Tab via URL — bidirecional. Filtros internos das tabs também são URL-driven (ver useUrlState).
  // Aceita deep-links de outras telas: tab=logs&search=&period=&level=&service=&alvo=&operation=&reqId=&desde=&ate=
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'requests';
  const setTab = useCallback((next: string) => {
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      if (next === 'requests') np.delete('tab');
      else np.set('tab', next);
      return np;
    }, { replace: true });
  }, [setSearchParams]);

  const handleRefresh = useCallback(() => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    Promise.allSettled([
      queryClient.refetchQueries({ queryKey: [...queryKeys.admin.logs] }),
      queryClient.refetchQueries({ queryKey: [...queryKeys.admin.requests] }),
    ]).finally(() => setIsRefreshing(false));
  }, [queryClient, isRefreshing]);

  return (
    <div className="space-y-6">
        <PageHeader
          title="Logs da Aplicação"
          subtitle="Visualize erros e eventos para investigação"
          icon={<ScrollText />}
          actions={
            <>
              {grafanaUrl && (
                <Button variant="outline" size="sm" asChild>
                  <a href={grafanaUrl} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Ver no Grafana</a>
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshCw className={`mr-2 h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Atualizar</span>
              </Button>
            </>
          }
        />

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList>
            <TabsTrigger value="logs" className="gap-2"><FileText className="h-4 w-4" /><span>Logs</span></TabsTrigger>
            <TabsTrigger value="requests" className="gap-2"><Activity className="h-4 w-4" /><span>Requisições</span></TabsTrigger>
            <TabsTrigger value="frontend-errors" className="gap-2">
              <AlertTriangle className="h-4 w-4" />
              <span className="hidden sm:inline">Erros Frontend</span><span className="sm:hidden">Erros</span>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="logs" className="mt-4"><LogsTab /></TabsContent>
          <TabsContent value="requests" className="mt-4"><RequestsTab /></TabsContent>
          <TabsContent value="frontend-errors" className="mt-4"><FrontendErrorsTab /></TabsContent>
        </Tabs>
    </div>
  );
}
